import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";

type NotificationType = 'discount' | 'cancellation' | 'stock_low' | 'critical_operation';

interface NotificationDetails {
  [key: string]: string | number | boolean | undefined;
}

export function useWhatsAppNotification() {
  const { activeRestaurant } = useRestaurantContext();

  const isNotificationEnabled = (type: NotificationType): boolean => {
    if (!activeRestaurant) return false;
    
    const restaurant = activeRestaurant as any;
    
    switch (type) {
      case 'discount':
        return restaurant.whatsapp_notify_discount !== false && !!restaurant.whatsapp_number;
      case 'cancellation':
        return restaurant.whatsapp_notify_cancellation !== false && !!restaurant.whatsapp_number;
      case 'stock_low':
        return restaurant.whatsapp_notify_low_stock !== false && !!restaurant.whatsapp_number;
      default:
        return !!restaurant.whatsapp_number;
    }
  };

  const sendNotification = async (
    notificationType: NotificationType,
    title: string,
    message: string,
    details?: NotificationDetails
  ) => {
    if (!activeRestaurant?.id) {
      console.log('No active restaurant, skipping WhatsApp notification');
      return;
    }

    // Check if this notification type is enabled
    if (!isNotificationEnabled(notificationType)) {
      console.log(`WhatsApp notification type "${notificationType}" is disabled`);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-whatsapp-notification', {
        body: {
          restaurant_id: activeRestaurant.id,
          notification_type: notificationType,
          title,
          message,
          details,
        },
      });

      if (error) {
        console.error('Error sending WhatsApp notification:', error);
      } else {
        console.log('WhatsApp notification result:', data);
      }
    } catch (err) {
      console.error('Failed to send WhatsApp notification:', err);
    }
  };

  const notifyDiscount = async (
    orderNumber: number,
    discountAmount: number,
    total: number,
    appliedBy?: string
  ) => {
    await sendNotification(
      'discount',
      'Desconto Aplicado',
      `Um desconto de R$ ${discountAmount.toFixed(2)} foi aplicado no pedido #${orderNumber}.`,
      {
        'Pedido': `#${orderNumber}`,
        'Desconto': `R$ ${discountAmount.toFixed(2)}`,
        'Total Final': `R$ ${total.toFixed(2)}`,
        'Aplicado por': appliedBy || 'Sistema',
      }
    );
  };

  const notifyCancellation = async (
    orderNumber: number,
    total: number,
    itemsCount: number,
    cancelledBy?: string
  ) => {
    await sendNotification(
      'cancellation',
      'Pedido Cancelado',
      `O pedido #${orderNumber} foi cancelado.`,
      {
        'Pedido': `#${orderNumber}`,
        'Valor': `R$ ${total.toFixed(2)}`,
        'Itens': itemsCount,
        'Cancelado por': cancelledBy || 'Sistema',
      }
    );
  };

  const notifyLowStock = async (
    ingredientName: string,
    currentStock: number,
    minimumStock: number,
    unit: string
  ) => {
    await sendNotification(
      'stock_low',
      'Estoque Baixo',
      `O ingrediente "${ingredientName}" está com estoque abaixo do mínimo.`,
      {
        'Ingrediente': ingredientName,
        'Estoque Atual': `${currentStock} ${unit}`,
        'Estoque Mínimo': `${minimumStock} ${unit}`,
      }
    );
  };

  return {
    sendNotification,
    notifyDiscount,
    notifyCancellation,
    notifyLowStock,
    isNotificationEnabled,
  };
}
